import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { FEATURED_INSIGHT, INSIGHT_THEMES, INSIGHTS } from "@/data/content";
import PageHero from "@/components/site/PageHero";
import CTABand from "@/components/site/CTABand";

export default function Insights() {
  useDocumentTitle("Insights | Thinking at the convergence — Meta61");
  const themes = ["All", ...INSIGHT_THEMES];
  const [filter, setFilter] = useState("All");
  const feed = filter === "All" ? INSIGHTS : INSIGHTS.filter((a) => a.theme === filter);

  return (
    <div>
      <PageHero
        title="Thinking at the"
        accent="convergence."
        subhead="Publications, articles, and perspectives on the technologies and frameworks shaping a trusted, sovereign, sustainable future."
      />

      {/* Featured */}
      <section className="m61-section">
        <div className="m61-container">
          <a href={FEATURED_INSIGHT.url} target="_blank" rel="noopener noreferrer"
            className="relative block overflow-hidden rounded-2xl border border-m61-line bg-m61-surface p-10 lg:p-16 group" data-testid="insights-featured">
            <div className="m61-grid-bg absolute inset-0 opacity-30" aria-hidden="true" />
            <div className="relative max-w-3xl">
              <p className="m61-eyebrow mb-5">{FEATURED_INSIGHT.eyebrow}</p>
              <h2 className="m61-h2 mb-4 group-hover:text-m61-cyan transition-colors">{FEATURED_INSIGHT.title}</h2>
              <p className="font-mono text-xs text-m61-muted uppercase tracking-wider mb-5">{FEATURED_INSIGHT.source} · {FEATURED_INSIGHT.date}</p>
              <p className="text-m61-muted leading-relaxed mb-8">{FEATURED_INSIGHT.summary}</p>
              <span className="m61-btn-primary">Read the article <ArrowUpRight className="w-4 h-4" /></span>
            </div>
          </a>
        </div>
      </section>

      {/* Feed */}
      <section className="m61-section border-t border-m61-line">
        <div className="m61-container">
          <div className="flex flex-wrap gap-2 mb-10">
            {themes.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  filter === t ? "border-m61-cyan text-m61-cyan bg-m61-cyan/10" : "border-m61-line text-m61-muted hover:border-m61-line-strong"
                }`}
                data-testid={`insights-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {feed.map((a) => (
              <a key={a.title} href={a.url} target="_blank" rel="noopener noreferrer" className="m61-card p-8 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[0.65rem] uppercase tracking-wider text-m61-violet">{a.theme}</span>
                  <span className="font-mono text-xs text-m61-muted">{a.date}</span>
                </div>
                <h3 className="m61-h3 mb-3">{a.title}</h3>
                <p className="text-m61-muted text-sm flex-1">{a.summary}</p>
                <span className="text-m61-cyan text-sm inline-flex items-center gap-2 mt-6">Read more <ArrowUpRight className="w-4 h-4" /></span>
              </a>
            ))}
          </div>
          {feed.length === 0 && <p className="text-m61-muted">No items in this theme yet.</p>}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
